import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box, Button, Typography, Paper, Select, MenuItem,
  FormControl, InputLabel, Chip, Stack, IconButton,
  Tooltip, CircularProgress, Alert, Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import * as pdfjsLib from 'pdfjs-dist';
import PdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';

pdfjsLib.GlobalWorkerOptions.workerSrc = PdfjsWorkerUrl;

const FIELD_TYPES = ['signature', 'initials', 'text', 'date', 'signerName', 'signerEmail', 'current_date', 'current_datetime'] as const;
type FieldType = typeof FIELD_TYPES[number];

const FIELD_LABELS: Record<FieldType, string> = {
  signature:        'Signature',
  initials:         'Initials',
  text:             'Text',
  date:             'Date',
  signerName:       'Signer Name',
  signerEmail:      'Signer Email',
  current_date:     'Current Date (auto)',
  current_datetime: 'Current Date & Time (auto)',
};

const FIELD_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00796b'];

const FIELD_SIZE: Record<FieldType, { w: number; h: number }> = {
  signature:        { w: 20, h: 8 },
  initials:         { w: 10, h: 8 },
  text:             { w: 20, h: 5 },
  date:             { w: 15, h: 5 },
  signerName:       { w: 20, h: 5 },
  signerEmail:      { w: 25, h: 5 },
  current_date:     { w: 15, h: 5 },
  current_datetime: { w: 22, h: 5 },
};

interface PlacedField {
  tempId: string;
  idSigningDocumentSigner: number;
  fieldType: FieldType;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
}

interface RenderedPage {
  pageNum: number;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

interface ResizeState {
  tempId: string;
  startMouseX: number;
  startMouseY: number;
  startW: number;
  startH: number;
  pageRect: DOMRect;
}

interface MoveState {
  tempId: string;
  startMouseX: number;
  startMouseY: number;
  startX: number;
  startY: number;
  fieldW: number;
  fieldH: number;
  pageRect: DOMRect;
  didMove: boolean;
}

const DocumentFieldPlacementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = Number(id);
  const navigate = useNavigate();
  const { documentSigningApiWrapper }: Api = useApi();
  const toast = useToastMessageQueue();

  const [doc, setDoc] = useState<any>(null);
  const [signers, setSigners] = useState<any[]>([]);
  const [fields, setFields] = useState<PlacedField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedSigner, setSelectedSigner] = useState<number | ''>('');
  const [selectedType, setSelectedType] = useState<FieldType>('signature');

  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [pdfPages, setPdfPages] = useState<RenderedPage[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const mountedRef = useRef(true);

  // Refs for page container elements (used to compute resize percentages)
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Active resize drag state
  const resizeRef = useRef<ResizeState | null>(null);

  // Active move drag state
  const moveRef = useRef<MoveState | null>(null);
  // Prevents the page click (place new field) firing right after a move drag ends
  const justMovedRef = useRef(false);

  // Fetch authenticated blob URL
  useEffect(() => {
    let objectUrl: string;
    documentSigningApiWrapper.getFileBlob('1.0', documentId).then((url) => {
      objectUrl = url;
      setFileUrl(url);
    });
    return () => {
      mountedRef.current = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  // Render PDF pages via PDF.js once blob URL is ready
  useEffect(() => {
    if (!fileUrl) return;
    mountedRef.current = true;
    setPdfLoading(true);

    const render = async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ url: fileUrl, withCredentials: false }).promise;
        const rendered: RenderedPage[] = [];

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext('2d')!, viewport, canvas }).promise;
          if (!mountedRef.current) return;
          rendered.push({ pageNum: i, canvas, width: viewport.width, height: viewport.height });
        }

        if (mountedRef.current) {
          setPdfPages(rendered);
          setPdfLoading(false);
        }
      } catch (e) {
        console.error('PDF render error', e);
        if (mountedRef.current) setPdfLoading(false);
      }
    };

    render();
  }, [fileUrl]);

  // Load document metadata and existing fields
  useEffect(() => {
    const load = async () => {
      try {
        const [docRes, fieldsRes] = await Promise.all([
          documentSigningApiWrapper.getDocument('1.0', documentId),
          documentSigningApiWrapper.getFields('1.0', documentId),
        ]);
        const d = docRes.data?.data;
        setDoc(d);
        setSigners(d?.signers ?? []);
        if (d?.signers?.length > 0) setSelectedSigner(d.signers[0].id);

        const existingFields: PlacedField[] = (fieldsRes.data?.data ?? []).map((f: any) => ({
          tempId: String(f.id),
          idSigningDocumentSigner: f.idSigningDocumentSigner,
          fieldType: f.fieldType,
          page: f.page,
          x: Number(f.x),
          y: Number(f.y),
          width: Number(f.width),
          height: Number(f.height),
          required: f.required,
        }));
        setFields(existingFields);
      } catch (err) {
        handleAPIError(err, toast, {});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [documentId]);

  // Global mouse handlers for field resizing and moving
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const r = resizeRef.current;
      if (r) {
        const dxPct = ((e.clientX - r.startMouseX) / r.pageRect.width) * 100;
        const dyPct = ((e.clientY - r.startMouseY) / r.pageRect.height) * 100;
        setFields(prev => prev.map(f =>
          f.tempId === r.tempId
            ? { ...f, width: Math.max(5, r.startW + dxPct), height: Math.max(3, r.startH + dyPct) }
            : f,
        ));
        return;
      }

      const m = moveRef.current;
      if (m) {
        const dxPct = ((e.clientX - m.startMouseX) / m.pageRect.width) * 100;
        const dyPct = ((e.clientY - m.startMouseY) / m.pageRect.height) * 100;
        if (Math.abs(dxPct) > 0.3 || Math.abs(dyPct) > 0.3) m.didMove = true;
        setFields(prev => prev.map(f =>
          f.tempId === m.tempId
            ? {
                ...f,
                x: Math.max(0, Math.min(100 - m.fieldW, m.startX + dxPct)),
                y: Math.max(0, Math.min(100 - m.fieldH, m.startY + dyPct)),
              }
            : f,
        ));
      }
    };

    const onMouseUp = () => {
      if (moveRef.current?.didMove || resizeRef.current) {
        justMovedRef.current = true;
        setTimeout(() => { justMovedRef.current = false; }, 0);
      }
      resizeRef.current = null;
      moveRef.current = null;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleMoveStart = (e: React.MouseEvent, field: PlacedField, pageNum: number) => {
    e.stopPropagation();
    e.preventDefault();
    const pageEl = pageRefs.current[pageNum];
    if (!pageEl) return;
    moveRef.current = {
      tempId: field.tempId,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: field.x,
      startY: field.y,
      fieldW: field.width,
      fieldH: field.height,
      pageRect: pageEl.getBoundingClientRect(),
      didMove: false,
    };
  };

  const handlePageClick = (e: React.MouseEvent<HTMLDivElement>, pageNum: number) => {
    if (justMovedRef.current || !selectedSigner) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    const size = FIELD_SIZE[selectedType];
    setFields(prev => [...prev, {
      tempId: `new-${Date.now()}`,
      idSigningDocumentSigner: selectedSigner as number,
      fieldType: selectedType,
      page: pageNum,
      x: Math.min(xPct, 100 - size.w),
      y: Math.min(yPct, 100 - size.h),
      width: size.w,
      height: size.h,
      required: true,
    }]);
  };

  const handleResizeStart = (
    e: React.MouseEvent,
    field: PlacedField,
    pageNum: number,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const pageEl = pageRefs.current[pageNum];
    if (!pageEl) return;
    resizeRef.current = {
      tempId: field.tempId,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startW: field.width,
      startH: field.height,
      pageRect: pageEl.getBoundingClientRect(),
    };
  };

  const removeField = (tempId: string) => {
    setFields(prev => prev.filter(f => f.tempId !== tempId));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await documentSigningApiWrapper.saveFields('1.0', documentId, fields.map(f => ({
        idSigningDocumentSigner: f.idSigningDocumentSigner,
        fieldType: f.fieldType,
        page: f.page,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        required: f.required,
      })));
      toast.success({ message: 'Fields saved.' });
      navigate('/document-signing');
    } catch (err) {
      handleAPIError(err, toast, {});
    } finally {
      setSaving(false);
    }
  };

  const signerColor = (signerId: number) => {
    const idx = signers.findIndex(s => s.id === signerId);
    return FIELD_COLORS[idx % FIELD_COLORS.length];
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!doc) return <Alert severity="error">Document not found.</Alert>;

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* ── Left: scrollable PDF canvas viewer ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#424242', overflow: 'hidden' }}>

        {/* top bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: '#303030', flexShrink: 0 }}>
          <IconButton size="small" sx={{ color: 'white' }} onClick={() => navigate('/document-signing')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'white', flex: 1 }}>{doc.title}</Typography>
          {pdfPages.length > 0 && (
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {pdfPages.length} page{pdfPages.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {/* PDF pages */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
          {pdfLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          )}
          {pdfPages.map(({ pageNum, canvas, width, height }) => {
            const pageFields = fields.filter(f => f.page === pageNum);
            return (
              <Box
                key={pageNum}
                sx={{
                  position: 'relative',
                  mx: 'auto',
                  mb: 3,
                  width: `${width}px`,
                  height: `${height}px`,
                  maxWidth: 'calc(100% - 32px)',
                  boxShadow: 6,
                }}
              >
                {/* page number badge */}
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute', top: -20, left: 0,
                    color: '#aaa', userSelect: 'none',
                  }}
                >
                  Page {pageNum}
                </Typography>

                {/* rendered PDF canvas */}
                <canvas
                  ref={el => {
                    if (el && el !== canvas) {
                      el.width = canvas.width;
                      el.height = canvas.height;
                      el.getContext('2d')?.drawImage(canvas, 0, 0);
                    }
                  }}
                  width={width}
                  height={height}
                  style={{ display: 'block', width: '100%', height: '100%' }}
                />

                {/* per-page click overlay for field placement */}
                <Box
                  ref={(el: HTMLDivElement | null) => { pageRefs.current[pageNum] = el; }}
                  onClick={(e) => handlePageClick(e, pageNum)}
                  sx={{
                    position: 'absolute', inset: 0,
                    cursor: selectedSigner ? 'crosshair' : 'not-allowed',
                    userSelect: 'none',
                  }}
                >
                  {pageFields.map(f => {
                    const color = signerColor(f.idSigningDocumentSigner);
                    return (
                      <Box
                        key={f.tempId}
                        onMouseDown={e => handleMoveStart(e, f, pageNum)}
                        sx={{
                          position: 'absolute',
                          left: `${f.x}%`,
                          top: `${f.y}%`,
                          width: `${f.width}%`,
                          height: `${f.height}%`,
                          border: `2px solid ${color}`,
                          bgcolor: `${color}33`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'move',
                          borderRadius: '2px',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color,
                            fontSize: '9px', fontWeight: 700, pointerEvents: 'none',
                          }}
                        >
                          {FIELD_LABELS[f.fieldType]}
                        </Typography>

                        {/* resize handle — bottom-right corner */}
                        <Box
                          onMouseDown={e => handleResizeStart(e, f, pageNum)}
                          onClick={e => e.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            bottom: -4,
                            right: -4,
                            width: 10,
                            height: 10,
                            bgcolor: color,
                            borderRadius: '2px',
                            cursor: 'nwse-resize',
                            zIndex: 1,
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Right: controls panel ── */}
      <Paper elevation={4} sx={{ width: 280, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Place Fields</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Select a signer and field type, then click anywhere on the document to place. Drag the corner handle to resize.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Signer</InputLabel>
            <Select value={selectedSigner} label="Signer" onChange={e => setSelectedSigner(e.target.value as number)}>
              {signers.map((s, i) => (
                <MenuItem key={s.id} value={s.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: FIELD_COLORS[i % FIELD_COLORS.length] }} />
                    {s.name || s.email}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Field Type</InputLabel>
            <Select value={selectedType} label="Field Type" onChange={e => setSelectedType(e.target.value as FieldType)}>
              {FIELD_TYPES.map(t => <MenuItem key={t} value={t}>{FIELD_LABELS[t]}</MenuItem>)}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Placed fields ({fields.length}) — click a field to remove it
          </Typography>

          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {fields.map(f => {
              const signer = signers.find(s => s.id === f.idSigningDocumentSigner);
              return (
                <Box key={f.tempId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: signerColor(f.idSigningDocumentSigner), flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    p{f.page} · {FIELD_LABELS[f.fieldType]} · {signer?.name || signer?.email || '?'}
                  </Typography>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => removeField(f.tempId)}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || fields.length === 0}
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default DocumentFieldPlacementPage;
